import { useMemo, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { WEEKDAY_ABBR, addMonths, dayNumber, monthLabel, monthMatrix, monthOf } from '../lib/calendar.ts';

export const chipColor = (c: string | null | undefined) => (!c ? 'var(--label-3)' : c.startsWith('#') ? c : `var(--ws-${c})`);

export interface GridItem {
  id: string;
  date: string;
  title: string;
  time: string | null;
  color: string;
  done?: boolean;
  draggable?: boolean;
}

export function MonthNav({ month, onChange, today, extra }: { month: string; onChange: (m: string) => void; today: string; extra?: ReactNode }) {
  return (
    <div className="month-nav">
      <button type="button" className="icon-btn" aria-label="Mese precedente" onClick={() => onChange(addMonths(month, -1))}><ChevronLeft size={22} /></button>
      <h2 aria-live="polite">{monthLabel(month)}</h2>
      <button type="button" className="icon-btn" aria-label="Mese successivo" onClick={() => onChange(addMonths(month, 1))}><ChevronRight size={22} /></button>
      {monthOf(today) !== month && (
        <button type="button" className="btn btn-plain btn-sm" onClick={() => onChange(monthOf(today))}>Oggi</button>
      )}
      {extra}
    </div>
  );
}

/**
 * Month view. On phones: compact grid with colored dots, tap a day to select it (the page shows its agenda).
 * On wide screens: items appear as chips inside the days; drag a chip to another day to reschedule.
 */
export function MonthGrid({
  month, today, selected, onSelect, items, onItemClick, onAdd, onDropItem, maxPerDay = 4,
}: {
  month: string;
  today: string;
  selected: string | null;
  onSelect: (date: string) => void;
  items: GridItem[];
  onItemClick: (item: GridItem) => void;
  onAdd?: (date: string) => void;
  onDropItem?: (id: string, date: string) => void;
  maxPerDay?: number;
}) {
  const weeks = useMemo(() => monthMatrix(month), [month]);
  const byDate = useMemo(() => {
    const m = new Map<string, GridItem[]>();
    for (const it of items) m.set(it.date, [...(m.get(it.date) ?? []), it]);
    return m;
  }, [items]);
  const [dropDate, setDropDate] = useState<string | null>(null);

  return (
    <div className="mgrid" role="grid" aria-label={monthLabel(month)}>
      <div className="mgrid-head" role="row">
        {WEEKDAY_ABBR.map((d) => <div key={d} role="columnheader">{d}</div>)}
      </div>
      {weeks.map((week) => (
        <div className="mgrid-week" role="row" key={week[0]}>
          {week.map((date) => {
            const dayItems = byDate.get(date) ?? [];
            const cls = ['mday', monthOf(date) !== month && 'out', date === today && 'today', date === selected && 'selected', dropDate === date && 'drop']
              .filter(Boolean).join(' ');
            return (
              <div
                key={date}
                role="gridcell"
                className={cls}
                aria-selected={date === selected}
                aria-label={`${dayNumber(date)}${dayItems.length ? `, ${dayItems.length} elementi` : ''}`}
                tabIndex={date === selected ? 0 : -1}
                onClick={() => onSelect(date)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(date)}
                onDragOver={onDropItem ? (e) => { e.preventDefault(); setDropDate(date); } : undefined}
                onDragLeave={onDropItem ? () => setDropDate((d) => (d === date ? null : d)) : undefined}
                onDrop={onDropItem ? (e) => {
                  e.preventDefault();
                  setDropDate(null);
                  const id = e.dataTransfer.getData('text/grid-item');
                  if (id) onDropItem(id, date);
                } : undefined}
              >
                {onAdd && (
                  <button type="button" className="add-in-day" aria-label="Aggiungi in questo giorno" onClick={(e) => { e.stopPropagation(); onAdd(date); }}>
                    <Plus size={15} />
                  </button>
                )}
                <span className="num">{dayNumber(date)}</span>
                <span className="dots" aria-hidden="true">
                  {dayItems.slice(0, 4).map((it) => <i key={it.id} style={{ '--dot': chipColor(it.color) } as React.CSSProperties} />)}
                </span>
                <span className="chips">
                  {dayItems.slice(0, maxPerDay).map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      className={`cal-chip${it.done ? ' done' : ''}`}
                      style={{ '--chip': chipColor(it.color) } as React.CSSProperties}
                      draggable={Boolean(it.draggable && onDropItem)}
                      onDragStart={(e) => e.dataTransfer.setData('text/grid-item', it.id)}
                      onClick={(e) => { e.stopPropagation(); onItemClick(it); }}
                      title={it.title}
                    >
                      {it.time && <span className="tm">{it.time}</span>}
                      <span className="t">{it.title}</span>
                    </button>
                  ))}
                  {dayItems.length > maxPerDay && (
                    <button type="button" className="more-link" onClick={(e) => { e.stopPropagation(); onSelect(date); }}>
                      +{dayItems.length - maxPerDay} altri
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
